/**
 * Safely parses JSON string with error handling and optional default value
 * @param jsonString - The JSON string to parse
 * @param defaultValue - The default value to return if parsing fails
 * @returns Parsed JSON object or default value
 */
export const safeJsonParse = <T = any>(
  jsonString: string | undefined | null,
  defaultValue: T
): T => {
  if (!jsonString) return defaultValue;
  
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('JSON parsing error:', error);
    console.error('Raw JSON string:', jsonString);
    console.error('String length:', jsonString.length);
    console.error('First 100 chars:', jsonString.substring(0, 100));
    console.error('Last 100 chars:', jsonString.substring(Math.max(0, jsonString.length - 100)));
    return defaultValue;
  }
};

/**
 * Safely parses a JSON string that should contain an array
 * @param jsonString - The JSON string to parse
 * @param defaultValue - The default array to return if parsing fails
 * @returns Parsed array or default value
 */
export const safeJsonParseArray = <T = any>(
  jsonString: string | undefined | null,
  defaultValue: T[] = []
): T[] => {
  const parsed = safeJsonParse(jsonString, defaultValue);
  return Array.isArray(parsed) ? parsed : defaultValue;
};

/**
 * Safely parses function arguments from tool calls
 * @param args - The arguments string to parse
 * @returns Parsed arguments object or empty object
 */
export const safeParseToolArgs = (args: string | undefined): Record<string, any> => {
  return safeJsonParse(args || '{}', {});
}; 